var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { InlineEditBase, handleInlineEditKeyDown, getInlineEditClassNames } from '../shared/InlineEditBase';
import { DEFAULT_INLINE_PLACEHOLDER } from '../shared/constants';
import './InlineTextStyles.scss';
/**
 * InlineText Component
 * Click-to-edit inline text input with multiple variants
 * Supports: text, currency, phone, number
 * @version 1.0.0
 * @since 2026-02-05
 */
export function InlineText(_a) {
    var { as, children, className, variant = 'text', width = 'auto', multiline = false, disableEdit = false, placeholder = DEFAULT_INLINE_PLACEHOLDER, label, spellCheck = true, id, 'aria-labelledby': ariaLabelledby, phoneValue, onPhoneChange, onEditStart, onEditEnd, numberValue, onNumberChange, allowDecimals = true, allowNegative = true, currencySymbol = '$' } = _a, props = __rest(_a, ["as", "children", "className", "variant", "width", "multiline", "disableEdit", "placeholder", "label", "spellCheck", "id", 'aria-labelledby', "phoneValue", "onPhoneChange", "onEditStart", "onEditEnd", "numberValue", "onNumberChange", "allowDecimals", "allowNegative", "currencySymbol"]);
    const Component = as || 'div';
    // Phone variant state and handlers
    const [isFocused, setIsFocused] = useState(false);
    const [originalPhoneValue, setOriginalPhoneValue] = useState(phoneValue || '');
    const [displayText, setDisplayText] = useState('');
    // Number variant ref
    const contentRef = useRef(null);
    // Phone variant helpers
    const normalizePhone = (input) => {
        const raw = (input || '').trim();
        if (!raw || raw === placeholder)
            return '';
        const hasPlus = raw.startsWith('+');
        const digits = raw.replace(/\D/g, '');
        return hasPlus ? `+${digits}` : digits;
    };
    const formatPhone = useMemo(() => (value) => {
        if (!value)
            return placeholder;
        const cleanPhone = value.replace(/\D/g, '');
        if (cleanPhone.length === 10) {
            return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
        }
        if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
            return `+1 (${cleanPhone.slice(1, 4)}) ${cleanPhone.slice(4, 7)}-${cleanPhone.slice(7)}`;
        }
        if (cleanPhone.length > 10) {
            const countryCode = cleanPhone.slice(0, -10);
            const remaining = cleanPhone.slice(-10);
            return `+${countryCode} ${remaining.slice(0, 3)} ${remaining.slice(3, 6)} ${remaining.slice(6)}`;
        }
        return value;
    }, [placeholder]);
    // Number variant helpers
    const formatNumber = (value) => {
        if (value === undefined || value === null || value === '')
            return '';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return Number.isNaN(numValue) ? '' : numValue.toString();
    };
    const validateNumberInput = (input) => {
        let cleaned = input;
        if (allowDecimals && allowNegative) {
            cleaned = cleaned.replace(/[^0-9.-]/g, '');
        }
        else if (allowDecimals) {
            cleaned = cleaned.replace(/[^0-9.]/g, '');
        }
        else if (allowNegative) {
            cleaned = cleaned.replace(/[^0-9-]/g, '');
        }
        else {
            cleaned = cleaned.replace(/[^0-9]/g, '');
        }
        if (allowDecimals) {
            const parts = cleaned.split('.');
            if (parts.length > 2) {
                cleaned = `${parts[0]}.${parts.slice(1).join('')}`;
            }
        }
        if (allowNegative) {
            const minusCount = (cleaned.match(/-/g) || []).length;
            if (minusCount > 1) {
                const firstMinus = cleaned.indexOf('-') === 0 ? '-' : '';
                cleaned = firstMinus + cleaned.replace(/-/g, '');
            }
            else if (cleaned.indexOf('-') > 0) {
                cleaned = cleaned.replace(/-/g, '');
            }
        }
        return cleaned;
    };
    // Phone variant effects
    useEffect(() => {
        if (variant === 'phone' && !isFocused) {
            const childText = typeof children === 'string' ? children : '';
            setDisplayText(formatPhone(phoneValue || childText || ''));
        }
    }, [variant, phoneValue, children, formatPhone, isFocused]);
    useEffect(() => {
        if (variant === 'phone') {
            setOriginalPhoneValue(phoneValue || '');
        }
    }, [variant, phoneValue]);
    // Number variant effect
    useEffect(() => {
        if (variant === 'number' && contentRef.current && document.activeElement !== contentRef.current) {
            contentRef.current.textContent = formatNumber(numberValue);
        }
    }, [variant, numberValue]);
    // Phone variant handlers
    const handlePhoneFocus = () => {
        setIsFocused(true);
        onEditStart === null || onEditStart === void 0 ? void 0 : onEditStart();
    };
    const handlePhoneBlur = (e) => {
        const currentValue = e.currentTarget.textContent || '';
        const normalized = normalizePhone(currentValue);
        const prevNormalized = normalizePhone(originalPhoneValue);
        if (normalized !== prevNormalized) {
            onPhoneChange === null || onPhoneChange === void 0 ? void 0 : onPhoneChange(normalized);
            setOriginalPhoneValue(normalized);
        }
        setDisplayText(formatPhone(normalized));
        setIsFocused(false);
        onEditEnd === null || onEditEnd === void 0 ? void 0 : onEditEnd();
    };
    // Number variant handlers
    const handleNumberInput = (e) => {
        if (disableEdit)
            return;
        const target = e.currentTarget;
        const inputValue = target.textContent || '';
        const validatedValue = validateNumberInput(inputValue);
        if (validatedValue !== inputValue) {
            target.textContent = validatedValue;
            const range = document.createRange();
            const selection = window.getSelection();
            if (selection) {
                range.selectNodeContents(target);
                range.collapse(false);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        }
    };
    // Number variant focus handler
    const handleNumberFocus = () => {
        onEditStart === null || onEditStart === void 0 ? void 0 : onEditStart();
    };
    const handleNumberBlur = (e) => {
        if (disableEdit) {
            onEditEnd === null || onEditEnd === void 0 ? void 0 : onEditEnd();
            return;
        }
        const value = e.currentTarget.textContent || '';
        const trimmedValue = value.trim();
        if (trimmedValue === '') {
            onNumberChange === null || onNumberChange === void 0 ? void 0 : onNumberChange('');
            onEditEnd === null || onEditEnd === void 0 ? void 0 : onEditEnd();
            return;
        }
        const numericValue = parseFloat(trimmedValue);
        if (!Number.isNaN(numericValue)) {
            onNumberChange === null || onNumberChange === void 0 ? void 0 : onNumberChange(numericValue);
        }
        else if (contentRef.current) {
            contentRef.current.textContent = formatNumber(numberValue);
        }
        onEditEnd === null || onEditEnd === void 0 ? void 0 : onEditEnd();
    };
    const handleNumberKeyDown = (e) => {
        var _a, _b;
        if (disableEdit)
            return;
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            (_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.blur();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            if (contentRef.current) {
                contentRef.current.textContent = formatNumber(numberValue);
            }
            (_b = e.currentTarget) === null || _b === void 0 ? void 0 : _b.blur();
        }
    };
    // Currency variant handlers
    const handleCurrencyFocus = () => {
        onEditStart === null || onEditStart === void 0 ? void 0 : onEditStart();
    };
    const handleCurrencyBlur = (e) => {
        // Get the edited content and propagate to parent if onBlur is provided in props
        const editedValue = e.currentTarget.textContent || '';
        // Call the props onBlur if it exists (for value persistence)
        if (props.onBlur) {
            props.onBlur(e);
        }
        onEditEnd === null || onEditEnd === void 0 ? void 0 : onEditEnd();
        // Note: The parent component is responsible for handling value changes
        // via the onBlur prop or by reading the DOM content
        void editedValue; // Acknowledge the value is available for parent handling
    };
    const variantClass = `qmb-ui-inline-edit--${variant}`;
    const computedClassName = getInlineEditClassNames(variantClass, width, multiline, className, disableEdit);
    // Phone variant render
    if (variant === 'phone') {
        return (React.createElement(Component, Object.assign({ className: computedClassName, contentEditable: !disableEdit, suppressContentEditableWarning: !disableEdit, "data-placeholder": placeholder, "data-label": label, onKeyDown: !disableEdit ? handleInlineEditKeyDown : undefined, onBlur: !disableEdit ? handlePhoneBlur : undefined, onFocus: !disableEdit ? handlePhoneFocus : undefined, spellCheck: !disableEdit ? spellCheck : false, id: id, role: !disableEdit ? 'textbox' : undefined, "aria-labelledby": !disableEdit && ariaLabelledby ? ariaLabelledby : undefined, "aria-label": !disableEdit && !ariaLabelledby && label ? label : undefined }, props), displayText));
    }
    // Number variant render
    if (variant === 'number') {
        const displayValue = formatNumber(numberValue) || children || placeholder;
        return (React.createElement(Component, Object.assign({ ref: contentRef, className: computedClassName, contentEditable: !disableEdit, suppressContentEditableWarning: !disableEdit, "data-placeholder": placeholder, "data-label": label, role: !disableEdit ? 'textbox' : undefined, tabIndex: disableEdit ? -1 : 0, "aria-multiline": !disableEdit ? false : undefined, inputMode: allowDecimals ? 'decimal' : 'numeric', spellCheck: false, autoCorrect: "off", autoCapitalize: "off", id: id, "aria-labelledby": !disableEdit && ariaLabelledby ? ariaLabelledby : undefined, "aria-label": !disableEdit && !ariaLabelledby && label ? label : undefined, onInput: !disableEdit ? handleNumberInput : undefined, onBlur: !disableEdit ? handleNumberBlur : undefined, onFocus: !disableEdit ? handleNumberFocus : undefined, onKeyDown: !disableEdit ? handleNumberKeyDown : undefined }, props), displayValue));
    }
    // Currency variant render
    if (variant === 'currency') {
        return (React.createElement(Component, Object.assign({ className: computedClassName, contentEditable: !disableEdit, suppressContentEditableWarning: !disableEdit, "data-placeholder": placeholder, "data-label": label, "data-currency": currencySymbol, onKeyDown: !disableEdit ? handleInlineEditKeyDown : undefined, onFocus: !disableEdit ? handleCurrencyFocus : undefined, onBlur: !disableEdit ? handleCurrencyBlur : undefined, spellCheck: !disableEdit ? spellCheck : false, id: id, role: !disableEdit ? 'textbox' : undefined, "aria-labelledby": !disableEdit && ariaLabelledby ? ariaLabelledby : undefined, "aria-label": !disableEdit && !ariaLabelledby && label ? label : undefined }, props), children));
    }
    // Default text variant render
    return (React.createElement(InlineEditBase, Object.assign({ as: as, variantClass: variantClass, width: width, multiline: multiline, disableEdit: disableEdit, placeholder: placeholder, label: label, spellCheck: spellCheck, id: id, "aria-labelledby": ariaLabelledby, className: className }, props), children));
}
